import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import usePagination from './usePagination';
import {
    getChats,
    getMessages,
    fetchMessage,
    getContacts,
    searchContacts,
    markMessageAsSeen,
    getUnseenMessages
} from 'services/chat';

const MAX_UNSEEN_MESSAGE_PAGES = 100;
const getNextPageToken = (response) =>
    response?.headers?.['pagination-token'] || response?.headers?.['next-page-token'];

/**
 * Hook: useChats
 * Lista de conversas (últimas mensagens por contato)
 */
export const useChats = (noPagination = false) => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const { isPending, isError, data, refetch } = useQuery({
        queryKey: ['chats', { paginationModel, noPagination }],
        queryFn: async () => {
            if (noPagination) {
                let allData = [];
                let pageToken = null;
                let hasNext = true;
                const seenTokens = new Set();

                while (hasNext) {
                    const resp = await getChats({
                        pageLimit: 50,
                        pageToken
                    });
                    allData = [...allData, ...(resp?.data || [])];
                    const nextToken = getNextPageToken(resp);
                    if (nextToken && !seenTokens.has(nextToken)) {
                        seenTokens.add(nextToken);
                        pageToken = nextToken;
                    } else {
                        hasNext = false;
                    }
                }
                return { data: allData };
            } else {
                return getChats({
                    pageLimit: paginationModel.pageSize,
                    pageToken: currentToken(paginationModel.page)
                });
            }
        }
    });

    useEffect(() => {
        if (!noPagination && data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data, noPagination, paginationModel.page, resolveHeader]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel,
        refetch
    };
};

/**
 *
 * Mensagens de uma conversa
 */
export const useMessages = (
    chatterUuid,
    noPagination = false,
    refetchInterval = 30000
) => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const { isPending, isError, data } = useQuery({
        queryKey: ['messages', { chatterUuid, paginationModel, noPagination }],
        queryFn: async () => {
            if (!chatterUuid) return { data: [] };

            if (noPagination) {
                let allData = [];
                let pageToken = null;
                let hasNext = true;
                const seenTokens = new Set();

                while (hasNext) {
                    const resp = await getMessages({
                        chatter: chatterUuid,
                        pageLimit: 50,
                        pageToken
                    });
                    allData = [...allData, ...(resp?.data || [])];
                    const nextToken = getNextPageToken(resp);
                    if (nextToken && !seenTokens.has(nextToken)) {
                        seenTokens.add(nextToken);
                        pageToken = nextToken;
                    } else {
                        hasNext = false;
                    }
                }
                return { data: allData };
            } else {
                return getMessages({
                    chatter: chatterUuid,
                    pageLimit: paginationModel.pageSize,
                    pageToken: currentToken(paginationModel.page)
                });
            }
        },
        enabled: Boolean(chatterUuid),
        refetchInterval
    });

    useEffect(() => {
        if (!noPagination && data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data, noPagination, paginationModel.page, resolveHeader]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    };
};

/**
 * All messages not yet read by the current role, newest first.
 */
export const useUnreadMessages = (roleUuid, refetchInterval = 30000) => {
    const { isPending, isError, data, refetch } = useQuery({
        queryKey: ['messages', 'unread', roleUuid],
        queryFn: async () => {
            const messages = [];
            const seenTokens = new Set();
            let pageToken = null;
            let fetchedCount = 0;

            for (let page = 0; page < MAX_UNSEEN_MESSAGE_PAGES; page += 1) {
                const response = await getUnseenMessages({ pageToken });
                const pageMessages = Array.isArray(response?.data) ? response.data : [];
                const totalCount = Number(response?.headers?.['pagination-count']);
                const nextToken = response?.headers?.['pagination-token'];

                messages.push(...pageMessages);
                fetchedCount += pageMessages.length;

                if (
                    pageMessages.length === 0 ||
                    (Number.isFinite(totalCount) && fetchedCount >= totalCount) ||
                    !nextToken ||
                    seenTokens.has(nextToken)
                ) {
                    break;
                }

                seenTokens.add(nextToken);
                pageToken = nextToken;
            }

            return messages
                .filter((message) => message.receiver?.uuid === roleUuid)
                .sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
        },
        enabled: Boolean(roleUuid),
        refetchInterval
    });

    return {
        data: data || [],
        isLoading: isPending,
        isError,
        refetch
    };
};

/**
 * Hook: useContacts
 */
export const useContacts = (noPagination = false, searchTerm = '', enabled = true) => {
    const {
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions,
        count,
        currentToken,
        resolveHeader
    } = usePagination();

    const { isPending, isError, data, refetch } = useQuery({
        queryKey: ['contacts', { paginationModel, noPagination, searchTerm }],
        queryFn: async () => {
            const method = searchTerm ? searchContacts : getContacts;
            const params = {
                pageLimit: paginationModel.pageSize,
                pageToken: currentToken(paginationModel.page)
            };

            if (noPagination) {
                let allData = [];
                let pageToken = null;
                let hasNext = true;
                const seenTokens = new Set();
                while (hasNext) {
                    const resp = await method({
                        ...params,
                        query: searchTerm,
                        pageLimit: 50,
                        pageToken
                    });
                    allData = [...allData, ...(resp?.data || [])];
                    const nextToken = getNextPageToken(resp);
                    if (nextToken && !seenTokens.has(nextToken)) {
                        seenTokens.add(nextToken);
                        pageToken = nextToken;
                    } else {
                        hasNext = false;
                    }
                }
                return { data: allData };
            } else {
                return method({
                    ...params,
                    query: searchTerm
                });
            }
        },
        enabled
    });

    useEffect(() => {
        if (!noPagination && data) {
            resolveHeader(data.headers, paginationModel.page);
        }
    }, [data, noPagination, paginationModel.page, resolveHeader]);

    return {
        data: data?.data || [],
        isLoading: isPending,
        error: isError,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel,
        refetch
    };
};

/**
 * Hook: useMessage
 * Procurar mensagem
 */
export const useMessage = (uuid, enabled = true) => {
    const { isPending, isError, data } = useQuery({
        queryKey: ['message', uuid],
        queryFn: () => fetchMessage(uuid),
        enabled: enabled && Boolean(uuid)
    });

    return {
        data: data?.data || null,
        isLoading: isPending,
        error: isError
    };
};

export const useMarkAsSeen = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (uuids) => {
            if (!Array.isArray(uuids)) uuids = [uuids];
            const filtered = uuids.filter(Boolean);
            if (!filtered.length) return;

            await Promise.all(filtered.map((id) => markMessageAsSeen(id)));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['messages'] });
            queryClient.invalidateQueries({ queryKey: ['chats'] });
        }
    });
};
